import { yupResolver } from "@hookform/resolvers/yup";
import { DEFAULT_SECTION_MARGINS, DynamicSectionBlock, DynamicSectionContent, SectionMargins } from "api/shop";
import { PaziaFromQuill } from "components/atoms/PaziaQuill/PaziaFromQuill";
import { TiptapTableEditor } from "components/atoms/TiptapTableEditor";
import {
  HeadingTagSelect,
  PaziaCheckbox,
  PaziaColorPicker,
  PaziaFormButton,
  PaziaInput,
} from "components/atoms";
import { ButtonVariantSelect } from "./ButtonVariantSelect";
import { Controller, FormProvider, useFieldArray, useForm } from "react-hook-form";
import { MdAdd, MdArrowDownward, MdArrowUpward, MdDelete } from "react-icons/md";
import * as yup from "yup";
import { MARGINS_FORM_NAME, marginsSchema, SectionMarginsConfig } from "./SectionMarginsConfig";

interface DynamicSectionFormProps {
  onSubmit: (data: DynamicSectionFormValues) => void;
  onCancel: () => void;
  defaultValues?: DynamicSectionContent;
  isLoading?: boolean;
}

const BLOCK_TYPES = [
  { value: "wysiwyg", label: "Tekst WYSIWYG" },
  { value: "table", label: "Tabela" },
  { value: "buttons", label: "Przyciski" },
  { value: "accordion", label: "Akordeon" },
];

export const DynamicSectionForm = ({ onSubmit, onCancel, defaultValues, isLoading }: DynamicSectionFormProps) => {
  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      headerText: defaultValues?.header?.text ?? "",
      headerTag: defaultValues?.header?.tag ?? "h2",
      blocks: defaultValues?.blocks?.map(convertBlockToFormValue) ?? [],
      [MARGINS_FORM_NAME]: (defaultValues as any)?.margins ?? DEFAULT_SECTION_MARGINS,
    },
  });
  const { handleSubmit, control, watch } = methods;

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "blocks",
  });

  const handleFormSubmit = (data: FormValues) => {
    const formattedData: DynamicSectionFormValues = {
      header: { text: data.headerText, tag: data.headerTag as any },
      blocks: data.blocks.map(convertFormValueToBlock),
      margins: data.margins,
    };
    onSubmit(formattedData);
  };

  const addBlock = (type: BlockType) => {
    if (type === "wysiwyg") {
      append({ type: "wysiwyg", content: "" });
    } else if (type === "table") {
      append({ type: "table", content: "" });
    } else if (type === "buttons") {
      append({ type: "buttons", buttons: [{ label: "", variant: "primary", href: "" }] });
    } else if (type === "accordion") {
      append({
        type: "accordion",
        items: [{ header: "", content: "" }],
        headerStyle: { color: "#333333", bold: false, hoverColor: "#3b82f6" },
      });
    }
  };

  const moveBlockUp = (index: number) => {
    if (index > 0) move(index, index - 1);
  };

  const moveBlockDown = (index: number) => {
    if (index < fields.length - 1) move(index, index + 1);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="grid gap-4">
        <PaziaInput name="headerText" label="Nagłówek sekcji" required />
        <HeadingTagSelect name="headerTag" />

        {/* Blocks */}
        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-medium text-slate-700 dark:text-slate-300">Bloki zawartości</h4>
            <div className="flex gap-2">
              {BLOCK_TYPES.map((bt) => (
                <button
                  key={bt.value}
                  type="button"
                  onClick={() => addBlock(bt.value as BlockType)}
                  className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
                >
                  <MdAdd size={16} />
                  {bt.label}
                </button>
              ))}
            </div>
          </div>

          {fields.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brak bloków. Dodaj blok klikając przyciski powyżej.
            </p>
          ) : (
            <div className="space-y-4">
              {fields.map((field, index) => {
                const blockType = watch(`blocks.${index}.type`);
                return (
                  <div
                    key={field.id}
                    className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-800"
                  >
                    <div className="mb-3 flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                        Blok #{index + 1} - {BLOCK_TYPES.find((bt) => bt.value === blockType)?.label}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => moveBlockUp(index)}
                          disabled={index === 0}
                          className="rounded p-1.5 text-slate-500 transition-colors hover:bg-slate-200 disabled:opacity-30 dark:hover:bg-slate-700"
                          title="Przesuń w górę"
                        >
                          <MdArrowUpward size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveBlockDown(index)}
                          disabled={index === fields.length - 1}
                          className="rounded p-1.5 text-slate-500 transition-colors hover:bg-slate-200 disabled:opacity-30 dark:hover:bg-slate-700"
                          title="Przesuń w dół"
                        >
                          <MdArrowDownward size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          className="rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                          title="Usuń blok"
                        >
                          <MdDelete size={18} />
                        </button>
                      </div>
                    </div>

                    {blockType === "wysiwyg" && <WysiwygBlockEditor index={index} />}
                    {blockType === "table" && <TableBlockEditor index={index} />}
                    {blockType === "buttons" && <ButtonsBlockEditor index={index} control={control} />}
                    {blockType === "accordion" && <AccordionBlockEditor index={index} control={control} />}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <SectionMarginsConfig />

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-600">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Anuluj
          </button>
          <PaziaFormButton disabled={isLoading} />
        </div>
      </form>
    </FormProvider>
  );
};

// Block editors
const WysiwygBlockEditor = ({ index }: { index: number }) => {
  return (
    <div>
      <PaziaFromQuill name={`blocks.${index}.content`} label="Zawartość" />
    </div>
  );
};

const TableBlockEditor = ({ index }: { index: number }) => {
  return (
    <Controller
      name={`blocks.${index}.content`}
      render={({ field }) => (
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Tabela</label>
          <TiptapTableEditor value={field.value} onChange={field.onChange} />
        </div>
      )}
    />
  );
};

const ButtonsBlockEditor = ({ index, control }: { index: number; control: any }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `blocks.${index}.buttons`,
  });

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Przyciski</label>
        <button
          type="button"
          onClick={() => append({ label: "", variant: "primary", href: "" })}
          className="flex items-center gap-1 rounded bg-slate-200 px-2 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
        >
          <MdAdd size={14} />
          Dodaj
        </button>
      </div>
      {fields.length === 0 ? (
        <p className="text-sm text-slate-500">Brak przycisków.</p>
      ) : (
        <div className="space-y-2">
          {fields.map((field, btnIndex) => (
            <div key={field.id} className="flex items-end gap-2 rounded border border-slate-200 bg-white p-2 dark:border-slate-600 dark:bg-slate-900">
              <div className="flex-1">
                <PaziaInput name={`blocks.${index}.buttons.${btnIndex}.label`} label="Etykieta" required />
              </div>
              <div className="w-32">
                <ButtonVariantSelect
                  name={`blocks.${index}.buttons.${btnIndex}.variant`}
                  required
                />
              </div>
              <div className="flex-1">
                <PaziaInput name={`blocks.${index}.buttons.${btnIndex}.href`} label="Link" required />
              </div>
              <button
                type="button"
                onClick={() => remove(btnIndex)}
                className="mb-1 rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30"
              >
                <MdDelete size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AccordionBlockEditor = ({ index, control }: { index: number; control: any }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `blocks.${index}.items`,
  });

  return (
    <div className="space-y-4">
      {/* Header Style Settings */}
      <div className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-600 dark:bg-slate-900">
        <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
          Styl nagłówków akordeonu
        </label>
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-40">
            <PaziaColorPicker name={`blocks.${index}.headerStyle.color`} label="Kolor tekstu" />
          </div>
          <div className="w-40">
            <PaziaColorPicker name={`blocks.${index}.headerStyle.hoverColor`} label="Kolor hover" />
          </div>
          <div className="flex items-center pt-5">
            <PaziaCheckbox name={`blocks.${index}.headerStyle.bold`} label="Pogrubiony" />
          </div>
        </div>
      </div>

      {/* Accordion Items */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Elementy akordeonu</label>
          <button
            type="button"
            onClick={() => append({ header: "", content: "" })}
            className="flex items-center gap-1 rounded bg-slate-200 px-2 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
          >
            <MdAdd size={14} />
            Dodaj element
          </button>
        </div>
        {fields.length === 0 ? (
          <p className="text-sm text-slate-500">Brak elementów akordeonu.</p>
        ) : (
          <div className="space-y-3">
            {fields.map((field, itemIndex) => (
              <div
                key={field.id}
                className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-600 dark:bg-slate-900"
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    Element #{itemIndex + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => remove(itemIndex)}
                    className="rounded p-1 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30"
                  >
                    <MdDelete size={16} />
                  </button>
                </div>
                <div className="space-y-3">
                  <PaziaInput
                    name={`blocks.${index}.items.${itemIndex}.header`}
                    label="Nagłówek"
                    required
                  />
                  <PaziaFromQuill
                    name={`blocks.${index}.items.${itemIndex}.content`}
                    label="Zawartość"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

type BlockType = "wysiwyg" | "table" | "buttons" | "accordion";

interface FormBlockValue {
  type: BlockType;
  content?: string;
  buttons?: Array<{ label: string; variant: string; href: string }>;
  items?: Array<{ header: string; content: string }>;
  headerStyle?: { color: string; bold: boolean; hoverColor: string };
}

interface FormValues {
  headerText: string;
  headerTag: string;
  blocks: FormBlockValue[];
  margins: SectionMargins;
}

const convertBlockToFormValue = (block: DynamicSectionBlock): FormBlockValue => {
  if (block.type === "buttons") {
    return { type: "buttons", buttons: block.buttons };
  }
  if (block.type === "accordion") {
    return { type: "accordion", items: block.items, headerStyle: block.headerStyle };
  }
  return { type: block.type, content: block.content };
};

const convertFormValueToBlock = (formValue: FormBlockValue): DynamicSectionBlock => {
  if (formValue.type === "buttons") {
    return {
      type: "buttons",
      buttons: (formValue.buttons ?? []).map((b) => ({
        label: b.label,
        variant: b.variant,
        href: b.href,
      })),
    };
  }
  if (formValue.type === "accordion") {
    return {
      type: "accordion",
      items: formValue.items ?? [],
      headerStyle: formValue.headerStyle ?? { color: "#333333", bold: false, hoverColor: "#3b82f6" },
    };
  }
  return { type: formValue.type, content: formValue.content ?? "" };
};

const blockSchema = yup.object({
  type: yup.string().oneOf(["wysiwyg", "table", "buttons", "accordion"]).required(),
  content: yup.string().when("type", {
    is: (val: string) => val === "wysiwyg" || val === "table",
    then: (schema) => schema,
    otherwise: (schema) => schema.notRequired(),
  }),
  buttons: yup.array().when("type", {
    is: "buttons",
    then: (schema) =>
      schema.of(
        yup.object({
          label: yup.string().required("Etykieta jest wymagana"),
          variant: yup.string().required(),
          href: yup.string().required("Link jest wymagany"),
        })
      ),
    otherwise: (schema) => schema.notRequired(),
  }),
  items: yup.array().when("type", {
    is: "accordion",
    then: (schema) =>
      schema.of(
        yup.object({
          header: yup.string().required("Nagłówek jest wymagany"),
          content: yup.string(),
        })
      ),
    otherwise: (schema) => schema.notRequired(),
  }),
  headerStyle: yup.object().when("type", {
    is: "accordion",
    then: (schema) =>
      schema.shape({
        color: yup.string().required(),
        bold: yup.boolean().required(),
        hoverColor: yup.string().required(),
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const schema = yup
  .object({
    headerText: yup.string().required("Nagłówek jest wymagany"),
    headerTag: yup.string(),
    blocks: yup.array().of(blockSchema),
    [MARGINS_FORM_NAME]: marginsSchema,
  })
  .required();

export interface DynamicSectionFormValues {
  header: { text: string; tag?: string };
  blocks: DynamicSectionBlock[];
  margins?: SectionMargins;
}
